import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Eye, 
  Trash2, 
  Download,
  Calendar,
  Clock,
  User,
  Target,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  MoreHorizontal,
  RefreshCw,
  FileText,
  Settings,
  Filter,
  Search,
  HelpCircle,
  ArrowUpRight, 
  ArrowDownRight
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import ActionMenu from '../common/ActionMenu';
import HelpButton from '../common/HelpButton';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';

// Custom Dropdown Component
const ActionDropdown = ({ task, onView, onEdit, onDelete}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 120);
    }
    setIsOpen((prev) => !prev);
  };

  const handleView = () => {
    setIsOpen(false);
    if (task && (task.id || task._id)) {
      onView(task);
    }
  };

  const handleEdit = () => {
    setIsOpen(false);
    if (task && (task.id || task._id)) {
      onEdit(task);
    }
  };
  
  const handleDelete = () => {
    setIsOpen(false);
    if (task && (task._id || task.id)) {
      onDelete(task);
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 10000 : 'auto' }}
    >
      {/* Action Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white text-blue-500 rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
      >
        <MoreHorizontal size={16} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight -
                  buttonRef.current.getBoundingClientRect().top +
                  8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                onClick={handleView}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View Task
              </button>
            </li>

            <li>
              <button
                onClick={handleEdit}
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit Task
              </button>
            </li>
            <li className="border-t border-gray-100 mt-1">
              <button
              onClick={handleDelete}
              className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
              >
                <Trash2 size={16} className="mr-2.5 text-red-600" />
                Delete Task
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const TaskManagement = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();
  
  // State management
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [stats, setStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    inProgressTasks: 0,
    overdueTasks: 0
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  
  // Load tasks on component mount
  useEffect(() => {
    loadTasks();
    loadStats();
  }, []);

  // Reload when pagination changes
  useEffect(() => {
    loadTasks();
  }, [pagination.currentPage, pagination.itemsPerPage]);

  const loadStats = async () => {
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.TASKS_STATS, {
        headers: API_CONFIG.HEADERS
      });
      
      if (response.ok) {
        const data = await response.json();
        const statsData = data.data || data;
        setStats({
          totalTasks: statsData.total || 0,
          completedTasks: statsData.byStatus?.done || 0,
          inProgressTasks: statsData.byStatus?.inProgress || 0,
          overdueTasks: statsData.overdue || 0
        });
      }
    } catch (error) {
      console.error('Error loading task stats:', error);
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pagination.currentPage.toString(),
        limit: pagination.itemsPerPage.toString()
      });

      const response = await fetch(`${API_CONFIG.ENDPOINTS.TASKS}?${queryParams}`, {
        headers: API_CONFIG.HEADERS
      });
      
      if (response.ok) {
        const data = await response.json();
        const responseData = data.data || data;
        const tasksList = responseData.tasks || responseData.data?.tasks || data.tasks || [];
        const paginationData = responseData.pagination || responseData;
        
        // Map backend task data to frontend format
        const mappedTasks = tasksList.map(task => {
          // Format dates
          const formatDate = (dateString) => {
            if (!dateString) return null;
            try {
              return new Date(dateString).toISOString().split('T')[0];
            } catch {
              return null;
            }
          };
          
          // Use progress from backend if explicitly set, otherwise calculate from hours
          let progress = task.progress;
          // Only calculate from hours if progress is not explicitly set (null or undefined)
          // Note: progress of 0 is a valid value, so we only calculate if it's null/undefined
          if ((progress === null || progress === undefined) && task.estimatedHours && task.actualHours) {
            progress = Math.min(Math.round((parseFloat(task.actualHours) / parseFloat(task.estimatedHours)) * 100), 100);
          }
          // Ensure progress is a number between 0 and 100, default to 0 if not set
          progress = progress !== null && progress !== undefined ? Math.min(Math.max(Number(progress), 0), 100) : 0;
          
          // Handle assignee - use assigneeName if available, otherwise populated assignee
          const assigneeDisplay = task.assigneeName || 
                                  (task.assignee 
                                    ? (typeof task.assignee === 'object' 
                                        ? (task.assignee.name || task.assignee.email || 'Unassigned')
                                        : task.assignee)
                                    : 'Unassigned');
          
          // Handle createdBy - use createdByName if available, otherwise populated createdBy
          const createdByDisplay = task.createdByName || 
                                   (task.createdBy 
                                     ? (typeof task.createdBy === 'object' 
                                         ? (task.createdBy.name || task.createdBy.email || 'Unknown')
                                         : task.createdBy)
                                     : 'Unknown');
          
          return {
            id: task._id || task.id,
            _id: task._id,
            title: task.title || 'Untitled Task',
            description: task.description || '',
            project: task.project?.name || 'No Project',
            projectId: task.project?._id || task.project?.id,
            projectCode: task.project?.projectCode || '',
            status: task.status || 'To Do',
            priority: task.priority || 'Medium',
            assignee: assigneeDisplay,
            assigneeId: task.assignee?._id || task.assignee?.id,
            createdBy: createdByDisplay,
            parentTask: task.parentTask?._id || task.parentTask,
            parentTaskTitle: task.parentTask?.title,
            dueDate: formatDate(task.dueDate),
            startDate: formatDate(task.startDate),
            completedDate: formatDate(task.completedDate),
            estimatedHours: task.estimatedHours || 0,
            actualHours: task.actualHours || 0,
            progress: progress,
            tags: task.tags || [],
            dependencies: task.dependencies || [],
            comments: task.comments?.length || 0,
            attachments: task.attachments?.length || 0
          };
        });
        
        setTasks(mappedTasks);
        setPagination(prev => ({
          ...prev,
          totalPages: paginationData.totalPages || 1,
          totalItems: paginationData.totalItems || paginationData.total || 0,
          currentPage: paginationData.currentPage || 1
        }));
        
        // Reload stats after tasks are loaded
        await loadStats();
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Failed to load tasks' }));
        throw new Error(errorData.message || 'Failed to load tasks');
      }
    } catch (err) {
      console.error('Error loading tasks:', err);
      showError(`Failed to load tasks: ${err.message}`);
      // Fallback to empty array on error
      setTasks([]);
      setPagination(prev => ({
        ...prev,
        totalPages: 1,
        totalItems: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = () => {
    navigate('/projects/task-management/create');
  };

  const handleViewTask = (task) => {
    const taskId = task._id || task.id;
    if (taskId) {
      navigate(`/projects/task-management/view/${taskId}`);
    } else {
      console.error('Task ID not found:', task);
      showError('Unable to view task: ID not found');
    }
  };

  const handleEditTask = (task) => {
    const taskId = task._id || task.id;
    if (taskId) {
      navigate(`/projects/task-management/edit/${taskId}`);
    } else {
      console.error('Task ID not found:', task);
      showError('Unable to edit task: ID not found');
    }
  };

  const handleDeleteTask = (task) => {
    setTaskToDelete(task);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!taskToDelete) return;
    
    setIsDeleting(true);
    try {
      const taskId = taskToDelete._id || taskToDelete.id;
      if (!taskId) {
        throw new Error('Task ID not found');
      }
      
      const response = await fetch(`${API_CONFIG.ENDPOINTS.TASKS}/${taskId}`, {
        method: 'DELETE',
        headers: API_CONFIG.HEADERS
      });
      
      const data = await response.json();
      
      if (response.ok) {
        success('Task deleted successfully!');
        // Reload tasks and stats
        await loadTasks();
        await loadStats();
        setShowDeleteModal(false);
        setTaskToDelete(null);
      } else {
        const errorMessage = data.message || data.data?.message || 'Failed to delete task';
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      showError(err.message || 'Failed to delete task');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRefresh = () => {
    loadTasks();
    loadStats();
  };

  const handleSearch = (searchTerm) => {
    // TODO: Implement search functionality with backend
    console.log('Search:', searchTerm);
    loadTasks();
  };

  const handlePageChange = (newPagination) => {
    setPagination(newPagination);
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/task-management';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh tasks list',
      disabled: loading
    },
    {
      label: 'New Task',
      icon: <Plus size={18} />,
      onClick: handleCreateTask,
      variant: 'primary',
      tooltip: 'Create a new task'
    }
  ];


  const getStatusBadge = (status) => {
  const statusConfig = {
    'To Do': { color: '#6b7280', bg: '#f3f4f6', text: 'To Do' },
    'In Progress': { color: '#3b82f6', bg: '#dbeafe', text: 'In Progress' },
    'Review': { color: '#f59e0b', bg: '#fef3c7', text: 'Review' },
    'Done': { color: '#10b981', bg: '#d1fae5', text: 'Done' },
    'Cancelled': { color: '#ef4444', bg: '#fee2e2', text: 'Cancelled' },
  };

  const config = statusConfig[status] || statusConfig['To Do'];

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.color}20`,
      }}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: config.color }}
      />
      {config.text}
    </span>
  );
};


  const getPriorityBadge = (priority) => {
  const priorityConfig = {
    High: { color: '#dc2626', bg: '#fee2e2', text: 'High' },
    Medium: { color: '#f59e0b', bg: '#fef3c7', text: 'Medium' },
    Low: { color: '#10b981', bg: '#d1fae5', text: 'Low' },
    Critical: { color: '#dc2626', bg: '#fee2e2', text: 'Critical' },
  };

  const config = priorityConfig[priority] || priorityConfig.Medium;

  return (
    <span
      className="inline-block px-1.5 py-0.5 text-xs font-medium rounded"
      style={{
        backgroundColor: config.bg,
        color: config.color,
        border: `1px solid ${config.color}20`,
      }}
    >
      {config.text}
    </span>
  );
};


  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'title',
      label: 'Task Title',
      render: (value, task) => (
        <div className="task-title-cell">
          <div className="task-title">
            {task.parentTask && (
              <span className="badge badge-secondary me-2" style={{ fontSize: '10px' }}>
                Subtask
              </span>
            )}
            {task.title}
          </div>
          <div className="task-project">
            {task.projectCode && <span className="text-muted">{task.projectCode} - </span>}
            {task.project}
            {task.parentTaskTitle && (
              <span className="text-muted ms-2">(of {task.parentTaskTitle})</span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, task) => getStatusBadge(task.status)
    },
    {
      key: 'priority',
      label: 'Priority',
      render: (value, task) => getPriorityBadge(task.priority)
    },
    {
      key: 'assignee',
      label: 'Assignee',
      render: (value, task) => (
        <div className="assignee-cell">
          <User size={16} />
          <span>{task.assignee}</span>
        </div>
      )
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      render: (value, task) => (
        <div className="date-cell">
          <Calendar size={16} />
          <span>{task.dueDate || 'No due date'}</span>
        </div>
      )
    },
    {
      key: 'progress',
      label: 'Progress',
      render: (value, task) => (
        <div className="progress-cell">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${task.progress}%` }}
            />
          </div>
          <span className="progress-text">{task.progress}%</span>
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, task) => {
        if (!task || !task.id) {
          console.error('Invalid task data in render function:', task);
          return <span style={{ color: 'red' }}>Error</span>;
        }
        return (
          <ActionDropdown 
            task={task}
            onView={handleViewTask}
            onEdit={handleEditTask}
            onDelete={handleDeleteTask}
          />
        );
      }
    }
  ];

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader 
        title="Task Management"
        description="Manage and track all your project tasks"
        actions={headerActions}
      />
      <div className="page-container">

      {/* Stats Cards */}
      <div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-6">
        {[
          {
            icon: Target,
            value: stats.totalTasks,
            label: 'Total Tasks',
            trend: '+8%',
            up: true,
          },
          {
            icon: CheckCircle,
            value: stats.completedTasks,
            label: 'Completed',
            trend: '+12%',
            up: true,
          },
          {
            icon: Clock,
            value: stats.inProgressTasks,
            label: 'In Progress',
            trend: '+5%',
            up: true,
          },
          {
            icon: AlertTriangle,
            value: stats.overdueTasks,
            label: 'Overdue',
            trend: '-3%',
            up: false,
          },
        ].map(({ icon: Icon, value, label, trend, up }, i) => (
        <div
        key={i}
        className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]"
        >
          {/* Accent line */}
          <div className="h-1 w-full bg-[var(--primary-color)]" />

          <div className="relative flex items-center gap-3 p-4">
          {/* Trend – SAME AS DASHBOARD */}
          <div
          className={`absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold ${
            up ? 'text-[#059669]' : 'text-[#dc2626]'
          }`}
          >
            {up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {trend}
          </div>

          {/* Icon */}
          <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
            <Icon size={20} className="text-[#2D7DD2]" />
            </div>

          {/* Text */}
          <div>
            <div className="text-[20px] font-bold text-[var(--text-primary)]">
              {value}
          </div>
          <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
            {label}
          </div>
          </div>
        </div>
       </div>
      ))}
</div>

      {/* Tasks Table */}
      <DataTable
        data={tasks}
        columns={columns}
        title="Tasks"
        loading={loading}
        pagination={pagination}
        onPaginationChange={handlePageChange}
        searchable={true}
        emptyMessage="No tasks found. Create your first task to get started."
        emptyIcon={<Target size={48} />}
        showActions={false}
        showCheckboxes={false}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Task"
        message={`Are you sure you want to delete "${taskToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={isDeleting}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default TaskManagement;